import { useState } from "react";
import { Layout } from "@jambushrushti/components/layout/Layout";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@jambushrushti/components/ui/card";
import { Button } from "@jambushrushti/components/ui/button";
import { Input } from "@jambushrushti/components/ui/input";
import { Label } from "@jambushrushti/components/ui/label";
import { Badge } from "@jambushrushti/components/ui/badge";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@jambushrushti/components/ui/tabs";
import { Skeleton } from "@jambushrushti/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@jambushrushti/components/ui/select";
import { useToast } from "@jambushrushti/hooks/use-toast";
import { Link } from "react-router-dom";
import {
  FileText,
  Search,
  Upload,
  BookOpen,
  ArrowLeft,
  Filter,
  Download,
  ExternalLink,
  FileUp,
  Tag,
  Languages,
  FolderOpen,
  Eye,
  Sparkles,
} from "lucide-react";

// Sample PDF books
const sampleBooks = [
  {
    id: 1,
    title: "Ācārāṅga Sūtra",
    publishTitle: "Ancient Jain Scriptures Vol. 1",
    author: "Traditional",
    publisher: "Jain Vishva Bharati",
    category: "Agama",
    language: "Prakrit",
    year: "1985",
    pages: 342,
    tags: ["Agamas", "Canonical", "Ethics"],
  },
  {
    id: 2,
    title: "Tattvartha Sutra",
    publishTitle: "Philosophy of Reality",
    author: "Ācārya Umāsvāti",
    publisher: "Jainology Department",
    category: "Philosophy",
    language: "Sanskrit",
    year: "1992",
    pages: 186,
    tags: ["Philosophy", "Metaphysics", "Tattvas"],
  },
  {
    id: 3,
    title: "Kalpa Sūtra",
    publishTitle: "Lives of Jinas",
    author: "Bhadrabāhu",
    publisher: "Prakrit Text Society",
    category: "Biography",
    language: "Prakrit",
    year: "1988",
    pages: 156,
    tags: ["Biography", "Mahāvīra", "Jinas"],
  },
];

// Sample search results
const sampleSearchResults = [
  {
    id: 1,
    bookTitle: "Ācārāṅga Sūtra",
    pageNumber: 45,
    snippet:
      "...आचार्य ने कहा कि **अहिंसा** परम धर्म है। जो प्राणी मात्र की रक्षा करता है, वही सच्चा साधु है...",
    matchedTerm: "अहिंसा",
    relevance: 95,
  },
  {
    id: 2,
    bookTitle: "Tattvartha Sutra",
    pageNumber: 23,
    snippet:
      "...सम्यग्दर्शन-ज्ञान-चारित्राणि मोक्षमार्गः। The path to liberation consists of right faith, right knowledge, and right conduct...",
    matchedTerm: "मोक्षमार्गः",
    relevance: 88,
  },
  {
    id: 3,
    bookTitle: "Kalpa Sūtra",
    pageNumber: 78,
    snippet:
      "...भगवान महावीर ने कहा - 'जीवन में **करुणा** और **क्षमा** का अभ्यास करो'...",
    matchedTerm: "करुणा",
    relevance: 82,
  },
  {
    id: 4,
    bookTitle: "Ācārāṅga Sūtra",
    pageNumber: 112,
    snippet:
      "...सभी जीवों के प्रति **दया** भाव रखना चाहिए। यह जैन धर्म का मूल सिद्धांत है...",
    matchedTerm: "दया",
    relevance: 79,
  },
];

const categories = [
  "Agama",
  "Philosophy",
  "Biography",
  "Commentary",
  "History",
  "Literature",
];
const languages = ["Prakrit", "Sanskrit", "Hindi", "Gujarati", "English"];

const Shabdasangraha = () => {
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState("search");
  const [searchQuery, setSearchQuery] = useState("");
  const [searchType, setSearchType] = useState("keyword");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedLanguage, setSelectedLanguage] = useState("");
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState<
    typeof sampleSearchResults
  >([]);
  const [showResults, setShowResults] = useState(false);

  // Upload form state
  const [uploadForm, setUploadForm] = useState({
    title: "",
    publishTitle: "",
    author: "",
    publisher: "",
    category: "",
    language: "",
    year: "",
    tags: "",
  });

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      toast({
        title: "Enter Search Query",
        description: "Please enter a keyword or phrase to search.",
        variant: "destructive",
      });
      return;
    }

    setIsSearching(true);
    setShowResults(true);
    setSearchResults([]);

    // Simulate AI-powered search delay
    await new Promise(resolve => setTimeout(resolve, 1500));

    setSearchResults(sampleSearchResults);
    setIsSearching(false);

    toast({
      title: `${sampleSearchResults.length} Results Found`,
      description: "Search completed across all PDFs.",
    });
  };

  const handleUpload = () => {
    if (!uploadForm.title.trim()) {
      toast({
        title: "Title Required",
        description: "Please enter a title for the PDF.",
        variant: "destructive",
      });
      return;
    }

    toast({
      title: "PDF Uploaded",
      description: "Your PDF has been uploaded and will be indexed shortly.",
    });

    setUploadForm({
      title: "",
      publishTitle: "",
      author: "",
      publisher: "",
      category: "",
      language: "",
      year: "",
      tags: "",
    });
  };

  const handleOpenPDF = (bookTitle: string, pageNumber: number) => {
    toast({
      title: "Opening PDF",
      description: `Opening ${bookTitle} at page ${pageNumber}...`,
    });
  };

  const handleDownloadPages = () => {
    toast({
      title: "Download Started",
      description: "Matched pages are being prepared for download.",
    });
  };

  return (
    <Layout>
      {/* Header */}
      <section className="bg-gradient-hero lotus-pattern py-12">
        <div className="container mx-auto px-4">
          <Link
            to="/research"
            className="mb-6 inline-flex items-center gap-2 text-muted-foreground transition-colors hover:text-primary"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Research Hub
          </Link>
          <div className="mb-4 flex items-center gap-4">
            <div className="bg-sage/10 flex h-16 w-16 items-center justify-center rounded-2xl">
              <FileText className="text-sage h-8 w-8" />
            </div>
            <div>
              <h1 className="font-poppins text-3xl font-bold text-foreground">
                Śabdasaṅgraha
              </h1>
              <p className="font-poppins text-muted-foreground">
                Smart PDF Search Engine
              </p>
            </div>
          </div>
          <p className="font-poppins max-w-2xl text-muted-foreground">
            Search inside Jain texts like Google. Find exact pages matching your
            query with AI-powered transliteration support.
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="bg-background py-8">
        <div className="container mx-auto px-4">
          <Tabs
            value={activeTab}
            onValueChange={setActiveTab}
            className="space-y-8"
          >
            <TabsList className="bg-secondary/50 p-1">
              <TabsTrigger
                value="search"
                className="font-poppins flex items-center gap-2"
              >
                <Search className="h-4 w-4" />
                Search
              </TabsTrigger>
              <TabsTrigger
                value="upload"
                className="font-poppins flex items-center gap-2"
              >
                <Upload className="h-4 w-4" />
                Upload
              </TabsTrigger>
              <TabsTrigger
                value="browse"
                className="font-poppins flex items-center gap-2"
              >
                <BookOpen className="h-4 w-4" />
                Browse
              </TabsTrigger>
            </TabsList>

            {/* Search Tab */}
            <TabsContent value="search" className="space-y-8">
              <Card className="shadow-soft">
                <CardContent className="p-8">
                  {/* Search Bar */}
                  <div className="mx-auto max-w-3xl space-y-6">
                    <div className="mb-8 text-center">
                      <h2 className="font-poppins mb-2 text-2xl font-semibold text-foreground">
                        Search Inside PDFs
                      </h2>
                      <p className="font-poppins text-muted-foreground">
                        Enter keywords in English, Hindi, or Gujarati with
                        transliteration support
                      </p>
                    </div>

                    <div className="relative">
                      <Search className="absolute left-4 top-1/2 h-6 w-6 -translate-y-1/2 text-muted-foreground" />
                      <Input
                        placeholder="अहिंसा परमो धर्म... or ahimsa paramo dharma..."
                        value={searchQuery}
                        onChange={e => setSearchQuery(e.target.value)}
                        className="font-poppins h-14 rounded-2xl border-2 pl-14 text-lg focus:border-primary"
                        onKeyDown={e => e.key === "Enter" && handleSearch()}
                      />
                    </div>

                    {/* Search Options */}
                    <div className="flex flex-wrap items-center justify-center gap-4">
                      <Select value={searchType} onValueChange={setSearchType}>
                        <SelectTrigger className="font-poppins w-44">
                          <SelectValue placeholder="Search type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="keyword" className="font-poppins">
                            Keyword Search
                          </SelectItem>
                          <SelectItem value="natural" className="font-poppins">
                            Natural Language
                          </SelectItem>
                          <SelectItem
                            value="transliteration"
                            className="font-poppins"
                          >
                            Transliteration
                          </SelectItem>
                        </SelectContent>
                      </Select>

                      <Select
                        value={selectedCategory}
                        onValueChange={setSelectedCategory}
                      >
                        <SelectTrigger className="font-poppins w-36">
                          <SelectValue placeholder="Category" />
                        </SelectTrigger>
                        <SelectContent>
                          {categories.map(cat => (
                            <SelectItem
                              key={cat}
                              value={cat}
                              className="font-poppins"
                            >
                              {cat}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>

                      <Select
                        value={selectedLanguage}
                        onValueChange={setSelectedLanguage}
                      >
                        <SelectTrigger className="font-poppins w-36">
                          <SelectValue placeholder="Language" />
                        </SelectTrigger>
                        <SelectContent>
                          {languages.map(lang => (
                            <SelectItem
                              key={lang}
                              value={lang}
                              className="font-poppins"
                            >
                              {lang}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>

                      <Button
                        variant="hero"
                        onClick={handleSearch}
                        disabled={isSearching}
                        className="font-poppins px-8"
                      >
                        {isSearching ? (
                          <>
                            <Sparkles className="mr-2 h-4 w-4 animate-spin" />
                            Searching...
                          </>
                        ) : (
                          <>
                            <Search className="mr-2 h-4 w-4" />
                            Search PDFs
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Search Results */}
              {showResults && (
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <p className="font-poppins text-muted-foreground">
                      {isSearching ? (
                        "Searching..."
                      ) : (
                        <>
                          <span className="font-semibold text-foreground">
                            {searchResults.length}
                          </span>{" "}
                          results found across PDFs
                        </>
                      )}
                    </p>
                    {!isSearching && searchResults.length > 0 && (
                      <Button
                        variant="outline"
                        onClick={handleDownloadPages}
                        className="font-poppins"
                      >
                        <Download className="mr-2 h-4 w-4" />
                        Download Matched Pages
                      </Button>
                    )}
                  </div>

                  {isSearching ? (
                    <div className="space-y-4">
                      {[1, 2, 3].map(i => (
                        <Card key={i} className="shadow-soft">
                          <CardContent className="p-6">
                            <div className="space-y-3">
                              <Skeleton className="h-6 w-48" />
                              <Skeleton className="h-4 w-32" />
                              <Skeleton className="h-16 w-full" />
                              <Skeleton className="h-8 w-24" />
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {searchResults.map(result => (
                        <Card
                          key={result.id}
                          className="shadow-soft hover:shadow-elevated transition-all duration-300"
                        >
                          <CardContent className="p-6">
                            <div className="flex items-start justify-between gap-4">
                              <div className="flex-1">
                                <div className="mb-2 flex items-center gap-3">
                                  <BookOpen className="h-5 w-5 text-primary" />
                                  <h3 className="font-poppins text-lg font-semibold text-foreground">
                                    {result.bookTitle}
                                  </h3>
                                  <Badge
                                    variant="secondary"
                                    className="font-poppins"
                                  >
                                    Page {result.pageNumber}
                                  </Badge>
                                  <Badge
                                    variant="outline"
                                    className="font-poppins bg-sage/10 text-sage border-sage/30 text-xs"
                                  >
                                    {result.relevance}% match
                                  </Badge>
                                </div>
                                <div className="mb-4 rounded-xl bg-secondary/30 p-4">
                                  <p className="font-poppins leading-relaxed text-foreground">
                                    {result.snippet}
                                  </p>
                                </div>
                                <div className="flex items-center gap-3">
                                  <Badge
                                    variant="outline"
                                    className="font-poppins text-xs"
                                  >
                                    <Tag className="mr-1 h-3 w-3" />
                                    {result.matchedTerm}
                                  </Badge>
                                </div>
                              </div>
                              <Button
                                variant="outline"
                                onClick={() =>
                                  handleOpenPDF(
                                    result.bookTitle,
                                    result.pageNumber
                                  )
                                }
                                className="font-poppins shrink-0"
                              >
                                <Eye className="mr-2 h-4 w-4" />
                                Open PDF
                              </Button>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </TabsContent>

            {/* Upload Tab */}
            <TabsContent value="upload">
              <Card className="shadow-soft mx-auto max-w-2xl">
                <CardHeader>
                  <CardTitle className="font-poppins flex items-center gap-2">
                    <FileUp className="h-5 w-5 text-primary" />
                    Upload New PDF
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Upload Area */}
                  <div className="cursor-pointer rounded-2xl border-2 border-dashed border-border p-8 text-center transition-colors hover:border-primary/50">
                    <Upload className="mx-auto mb-4 h-12 w-12 text-muted-foreground" />
                    <p className="font-poppins mb-1 font-medium text-foreground">
                      Drop your PDF here or click to browse
                    </p>
                    <p className="font-poppins text-sm text-muted-foreground">
                      Maximum file size: 50MB
                    </p>
                  </div>

                  {/* Metadata Form */}
                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-2">
                      <Label className="font-poppins">Title *</Label>
                      <Input
                        placeholder="Book title"
                        value={uploadForm.title}
                        onChange={e =>
                          setUploadForm({
                            ...uploadForm,
                            title: e.target.value,
                          })
                        }
                        className="font-poppins"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="font-poppins">Publish Title</Label>
                      <Input
                        placeholder="Publication title"
                        value={uploadForm.publishTitle}
                        onChange={e =>
                          setUploadForm({
                            ...uploadForm,
                            publishTitle: e.target.value,
                          })
                        }
                        className="font-poppins"
                      />
                    </div>
                  </div>

                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-2">
                      <Label className="font-poppins">Author</Label>
                      <Input
                        placeholder="Author name"
                        value={uploadForm.author}
                        onChange={e =>
                          setUploadForm({
                            ...uploadForm,
                            author: e.target.value,
                          })
                        }
                        className="font-poppins"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="font-poppins">Publisher</Label>
                      <Input
                        placeholder="Publisher name"
                        value={uploadForm.publisher}
                        onChange={e =>
                          setUploadForm({
                            ...uploadForm,
                            publisher: e.target.value,
                          })
                        }
                        className="font-poppins"
                      />
                    </div>
                  </div>

                  <div className="grid gap-4 md:grid-cols-3">
                    <div className="space-y-2">
                      <Label className="font-poppins">Category</Label>
                      <Select
                        value={uploadForm.category}
                        onValueChange={v =>
                          setUploadForm({ ...uploadForm, category: v })
                        }
                      >
                        <SelectTrigger className="font-poppins">
                          <SelectValue placeholder="Select" />
                        </SelectTrigger>
                        <SelectContent>
                          {categories.map(cat => (
                            <SelectItem
                              key={cat}
                              value={cat}
                              className="font-poppins"
                            >
                              {cat}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="font-poppins">Language</Label>
                      <Select
                        value={uploadForm.language}
                        onValueChange={v =>
                          setUploadForm({ ...uploadForm, language: v })
                        }
                      >
                        <SelectTrigger className="font-poppins">
                          <SelectValue placeholder="Select" />
                        </SelectTrigger>
                        <SelectContent>
                          {languages.map(lang => (
                            <SelectItem
                              key={lang}
                              value={lang}
                              className="font-poppins"
                            >
                              {lang}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="font-poppins">Year</Label>
                      <Input
                        placeholder="Publication year"
                        value={uploadForm.year}
                        onChange={e =>
                          setUploadForm({ ...uploadForm, year: e.target.value })
                        }
                        className="font-poppins"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label className="font-poppins">Tags</Label>
                    <Input
                      placeholder="Enter tags separated by commas"
                      value={uploadForm.tags}
                      onChange={e =>
                        setUploadForm({ ...uploadForm, tags: e.target.value })
                      }
                      className="font-poppins"
                    />
                  </div>

                  <div className="flex justify-end gap-3 border-t pt-4">
                    <Button
                      variant="outline"
                      onClick={() =>
                        setUploadForm({
                          title: "",
                          publishTitle: "",
                          author: "",
                          publisher: "",
                          category: "",
                          language: "",
                          year: "",
                          tags: "",
                        })
                      }
                      className="font-poppins"
                    >
                      Reset
                    </Button>
                    <Button
                      variant="hero"
                      onClick={handleUpload}
                      className="font-poppins"
                    >
                      <Upload className="mr-2 h-4 w-4" />
                      Upload PDF
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Browse Tab */}
            <TabsContent value="browse">
              <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                {sampleBooks.map(book => (
                  <Card
                    key={book.id}
                    className="shadow-soft hover:shadow-elevated group transition-all duration-300"
                  >
                    <CardContent className="p-6">
                      <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 transition-transform group-hover:scale-110">
                        <BookOpen className="h-6 w-6 text-primary" />
                      </div>
                      <h3 className="font-poppins mb-1 text-lg font-semibold text-foreground">
                        {book.title}
                      </h3>
                      <p className="font-poppins mb-3 text-sm text-muted-foreground">
                        {book.author}
                      </p>
                      <div className="mb-4 flex flex-wrap gap-2">
                        <Badge
                          variant="secondary"
                          className="font-poppins text-xs"
                        >
                          <Languages className="mr-1 h-3 w-3" />
                          {book.language}
                        </Badge>
                        <Badge
                          variant="outline"
                          className="font-poppins text-xs"
                        >
                          <FolderOpen className="mr-1 h-3 w-3" />
                          {book.category}
                        </Badge>
                        <Badge
                          variant="outline"
                          className="font-poppins text-xs"
                        >
                          {book.pages} pages
                        </Badge>
                      </div>
                      <div className="mb-4 flex flex-wrap gap-1">
                        {book.tags.map(tag => (
                          <Badge
                            key={tag}
                            variant="secondary"
                            className="font-poppins text-xs"
                          >
                            {tag}
                          </Badge>
                        ))}
                      </div>
                      <Button
                        variant="outline"
                        className="font-poppins w-full"
                        onClick={() => handleOpenPDF(book.title, 1)}
                      >
                        <Eye className="mr-2 h-4 w-4" />
                        View PDF
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </section>
    </Layout>
  );
};

export default Shabdasangraha;
